// src/hooks/usePaginatedQuery.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DocumentData, DocumentSnapshot, Query } from 'firebase/firestore';
import { getDocs, limit, query, startAfter } from 'firebase/firestore';

type UsePaginatedQueryOpts<T> = {
  pageSize: number;
  // Convert Firestore doc -> your type
  mapDoc: (doc: DocumentSnapshot<DocumentData>) => T;
  // Optional: reset when these change (e.g. active tab, filters)
  deps?: any[];
};

export function usePaginatedQuery<T>(
  baseQuery: Query<DocumentData> | null,
  opts: UsePaginatedQueryOpts<T>,
) {
  const { pageSize, mapDoc, deps = [] } = opts;

  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const lastDocRef = useRef<DocumentSnapshot<DocumentData> | null>(null);

  // ✅ Critical: keep latest mapDoc in a ref so it doesn't cause dependency churn
  const mapDocRef = useRef(mapDoc);
  useEffect(() => {
    mapDocRef.current = mapDoc;
  }, [mapDoc]);

  const reset = useCallback(() => {
    setItems([]);
    setHasMore(false);
    lastDocRef.current = null;
  }, []);

  const loadFirstPage = useCallback(async () => {
    if (!baseQuery) return;
    setIsLoading(true);
    try {
      const q = query(baseQuery, limit(pageSize));
      const snap = await getDocs(q);

      const mapper = mapDocRef.current;
      const nextItems = snap.docs.map(mapper);
      setItems(nextItems);

      lastDocRef.current = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
      setHasMore(snap.docs.length === pageSize);
    } finally {
      setIsLoading(false);
    }
  }, [baseQuery, pageSize]);

  const loadMore = useCallback(async () => {
    if (!baseQuery) return;
    if (!hasMore) return;
    if (isLoadingMore) return;

    const cursor = lastDocRef.current;
    if (!cursor) return;

    setIsLoadingMore(true);
    try {
      const q = query(baseQuery, startAfter(cursor), limit(pageSize));
      const snap = await getDocs(q);

      const mapper = mapDocRef.current;
      const more = snap.docs.map(mapper);
      setItems((prev) => [...prev, ...more]);

      lastDocRef.current = snap.docs.length ? snap.docs[snap.docs.length - 1] : cursor;
      setHasMore(snap.docs.length === pageSize);
    } finally {
      setIsLoadingMore(false);
    }
  }, [baseQuery, pageSize, hasMore, isLoadingMore]);

  // ✅ auto-reset + reload when deps change
  useEffect(() => {
    reset();
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseQuery, pageSize, reset, loadFirstPage, ...deps]);

  return {
    items,
    isLoading,
    hasMore,
    isLoadingMore,
    loadMore,
    reset,
    reload: loadFirstPage,
  };
}
